import { useCallback, useEffect, useRef, useState } from 'react';

export const TEXT_COLORS = [
    '#ffffff',
    '#000000',
    '#ff3b30',
    '#ff9500',
    '#ffcc00',
    '#34c759',
    '#007aff',
    '#af52de',
    '#ff2d55',
];

export const BG_MODE = {
    DARK: 'dark',
    COLORED: 'colored',
    NONE: 'none',
};

export const BG_MODE_CYCLE = [BG_MODE.DARK, BG_MODE.COLORED, BG_MODE.NONE];

export const FONT = { MIN: 14, MAX: 72, STEP: 4, DEFAULT: 28 };

export const DEFAULT_TEXT_COLOR = '#ffffff';
export const DEFAULT_BG_MODE = BG_MODE.DARK;

export const DRAG_THRESHOLD_PX = 5;

let _idCounter = 1;
const generateTextId = () => _idCounter++;

export const createTextItem = (color = DEFAULT_TEXT_COLOR, bgMode = DEFAULT_BG_MODE, fontSize = FONT.DEFAULT) => ({
    id: generateTextId(),
    content: '',
    position: { x: 50, y: 45 },
    color,
    bgMode,
    fontSize,
});

export const initializeTextItems = savedItems => {
    if (savedItems?.length > 0) {
        return savedItems.map(item => ({ ...item, id: generateTextId() }));
    }
    return [createTextItem()];
};

export const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export const computeImageDisplayArea = (containerEl, mediaEl) => {
    if (!containerEl || !mediaEl) return null;

    const containerWidth = containerEl.offsetWidth;
    const containerHeight = containerEl.offsetHeight;
    const naturalWidth = mediaEl.naturalWidth ?? mediaEl.videoWidth ?? 0;
    const naturalHeight = mediaEl.naturalHeight ?? mediaEl.videoHeight ?? 0;

    if (!naturalWidth || !naturalHeight) return null;

    const mediaAspect = naturalWidth / naturalHeight;
    const containerAspect = containerWidth / containerHeight;

    let displayWidth, displayHeight, offsetLeft, offsetTop;

    if (mediaAspect > containerAspect) {
        displayWidth = containerWidth;
        displayHeight = containerWidth / mediaAspect;
        offsetLeft = 0;
        offsetTop = (containerHeight - displayHeight) / 2;
    } else {
        displayHeight = containerHeight;
        displayWidth = containerHeight * mediaAspect;
        offsetLeft = (containerWidth - displayWidth) / 2;
        offsetTop = 0;
    }

    return { left: offsetLeft, top: offsetTop, width: displayWidth, height: displayHeight };
};

export const getPointerPositionInArea = (event, containerEl, displayArea) => {
    if (!containerEl || !displayArea) return null;

    const containerRect = containerEl.getBoundingClientRect();
    const source = event.touches ? event.touches[0] : event;

    return {
        x: source.clientX - containerRect.left - displayArea.left,
        y: source.clientY - containerRect.top - displayArea.top,
    };
};

export const resolveTextInputStyle = textItem => {
    const base = { fontSize: textItem.fontSize ?? FONT.DEFAULT, color: textItem.color };

    switch (textItem.bgMode) {
        case BG_MODE.DARK:
            return { ...base, background: 'rgba(0,0,0,0.6)' };
        case BG_MODE.COLORED:
            return { ...base, color: '#fff', background: textItem.color };
        default:
            return { ...base, background: 'transparent' };
    }
};

export const getNextBgMode = currentMode => {
    const currentIndex = BG_MODE_CYCLE.indexOf(currentMode);
    return BG_MODE_CYCLE[(currentIndex + 1) % BG_MODE_CYCLE.length];
};

export const getBgButtonLabel = bgMode => {
    if (bgMode === BG_MODE.DARK) return 'A▪';
    if (bgMode === BG_MODE.COLORED) return 'A●';
    return 'A';
};

export const drawTextItemOnCanvas = (ctx, textItem, canvasWidth, canvasHeight, displayAreaWidth) => {
    const x = (textItem.position.x / 100) * canvasWidth;
    const y = (textItem.position.y / 100) * canvasHeight;

    const scale = canvasWidth / displayAreaWidth;
    const fontSize = Math.max((textItem.fontSize ?? FONT.DEFAULT) * scale, textItem.fontSize ?? FONT.DEFAULT);

    ctx.font = `bold ${fontSize}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const metrics = ctx.measureText(textItem.content);
    const paddingH = fontSize * 0.43;
    const paddingV = fontSize * 0.21;
    const boxWidth = metrics.width + paddingH * 2;
    const boxHeight = fontSize * 1.25 + paddingV * 2;
    const borderRadius = fontSize * 0.21;

    if (textItem.bgMode === BG_MODE.DARK) {
        ctx.fillStyle = 'rgba(0,0,0,0.6)';
        ctx.beginPath();
        ctx.roundRect
            ? ctx.roundRect(x - boxWidth / 2, y - boxHeight / 2, boxWidth, boxHeight, borderRadius)
            : ctx.rect(x - boxWidth / 2, y - boxHeight / 2, boxWidth, boxHeight);
        ctx.fill();
    } else if (textItem.bgMode === BG_MODE.COLORED) {
        ctx.fillStyle = textItem.color;
        ctx.globalAlpha = 0.85;
        ctx.fillRect(x - boxWidth / 2, y - boxHeight / 2, boxWidth, boxHeight);
        ctx.globalAlpha = 1;
    }

    ctx.fillStyle = textItem.bgMode === BG_MODE.COLORED ? '#fff' : textItem.color;
    ctx.fillText(textItem.content, x, y);
};

export const useImageDisplayArea = (containerRef, mediaRef) => {
    const [displayArea, setDisplayArea] = useState(null);

    const recalculate = useCallback(() => {
        const area = computeImageDisplayArea(containerRef.current, mediaRef.current);
        if (area) setDisplayArea(area);
    }, [containerRef, mediaRef]);

    useEffect(() => {
        const media = mediaRef.current;
        if (media && (media.complete || media.readyState >= 2)) recalculate();
    }, [recalculate, mediaRef]);

    useEffect(() => {
        window.addEventListener('resize', recalculate);
        return () => window.removeEventListener('resize', recalculate);
    }, [recalculate]);

    return { displayArea, onMediaLoad: recalculate };
};

export const useTextDrag = (containerRef, displayArea, onPositionChange, onActivate) => {
    const dragState = useRef(null);

    const onDragStart = (event, itemId, currentPosition) => {
        const pointerInArea = getPointerPositionInArea(event, containerRef.current, displayArea);
        if (!pointerInArea) return;

        dragState.current = {
            itemId,
            startPointer: pointerInArea,
            startPosition: { ...currentPosition },
            hasMoved: false,
        };

        onActivate?.(itemId);
    };

    const onDragMove = event => {
        if (!dragState.current || !displayArea) return;

        const pointerInArea = getPointerPositionInArea(event, containerRef.current, displayArea);
        if (!pointerInArea) return;

        const { itemId, startPointer, startPosition } = dragState.current;
        const deltaX = pointerInArea.x - startPointer.x;
        const deltaY = pointerInArea.y - startPointer.y;

        // Ignore tiny movements to allow normal text cursor clicks
        if (!dragState.current.hasMoved && Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD_PX) return;
        dragState.current.hasMoved = true;

        onPositionChange(itemId, {
            x: clamp(startPosition.x + (deltaX / displayArea.width) * 100, 5, 95),
            y: clamp(startPosition.y + (deltaY / displayArea.height) * 100, 5, 95),
        });
    };

    const onDragEnd = () => {
        dragState.current = null;
    };

    return { onDragStart, onDragMove, onDragEnd };
};

export const rotateImage = (src, rotation, mimeType = 'image/jpeg') => {
    return new Promise((resolve, reject) => {
        const image = new Image();

        image.onload = () => {
            const radians = (rotation * Math.PI) / 180;

            const sin = Math.abs(Math.sin(radians));
            const cos = Math.abs(Math.cos(radians));

            const canvas = document.createElement('canvas');

            canvas.width = image.width * cos + image.height * sin;
            canvas.height = image.width * sin + image.height * cos;

            const ctx = canvas.getContext('2d');

            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.rotate(radians);

            ctx.drawImage(image, -image.width / 2, -image.height / 2);

            canvas.toBlob(
                blob => {
                    if (!blob) {
                        reject(new Error('Rotation failed'));
                        return;
                    }

                    resolve(blob);
                },
                mimeType,
                1,
            );
        };

        image.onerror = reject;
        image.src = src;
    });
};

export const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
};

export const drawStroke = (ctx, points, color, size) => {
    if (!points?.length) return;

    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = size;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (points.length === 1) {
        ctx.arc(points[0].x, points[0].y, size / 2, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
        return;
    }

    ctx.moveTo(points[0].x, points[0].y);

    for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.stroke();
};

export const getContainArea = (canvas, img) => {
    const imgAspect = img.naturalWidth / img.naturalHeight;
    const canvasAspect = canvas.width / canvas.height;
    let dx = 0,
        dy = 0,
        dw = canvas.width,
        dh = canvas.height;
    if (imgAspect > canvasAspect) {
        dh = canvas.width / imgAspect;
        dy = (canvas.height - dh) / 2;
    } else {
        dw = canvas.height * imgAspect;
        dx = (canvas.width - dw) / 2;
    }
    return { dx, dy, dw, dh };
};

export const compositeStrokes = (canvas, strokes) => {
    if (!strokes?.length) return;

    const tmp = document.createElement('canvas');
    tmp.width = canvas.width;
    tmp.height = canvas.height;

    const tmpCtx = tmp.getContext('2d');

    strokes.forEach(s => {
        if (s.type === 'eraser') {
            tmpCtx.globalCompositeOperation = 'destination-out';
            drawStroke(tmpCtx, s.points, 'rgba(0,0,0,1)', s.size);
            tmpCtx.globalCompositeOperation = 'source-over';
        } else if (s.type === 'marker') {
            drawStroke(tmpCtx, s.points, hexToRgba(s.color, 0.5), s.size * 1.8);
        } else {
            drawStroke(tmpCtx, s.points, s.color, s.size);
        }
    });
    canvas.getContext('2d').drawImage(tmp, 0, 0);
};
